import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { Loader2 } from "lucide-react"
import { api_fetch_throws } from "../../../api/client"
import { UserState } from "../../../auth/user"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { isSaffronRoadUser } from "../../../utils"
import { KeheTab } from "./kehe_tab"
import { KrogerTab } from "./kroger_tab"
import { MatchTab } from "./match_tab"
import { MethodTab } from "./method_tab"
import { OverviewTab } from "./overview_tab"
import { promo_lift_review } from "./promo_lift_review_model"
import { Fig } from "./promo_lift_review_parts"

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "kehe", label: "KeHE lift" },
  { value: "kroger", label: "Kroger lift" },
  { value: "match", label: "Matched promo spend" },
  { value: "method", label: "How we measured" },
]

/** Saffron Road's promo lift review: a fixed snapshot the server only hands to this brand. */
export function PromoLiftReviewPage() {
  const org_id = UserState.use((user) => user?.org_id)
  const allowed = isSaffronRoadUser(org_id)
  const { data, error } = useSWR<promo_lift_review>(allowed ? "/promo-lift-review" : null, (path: string) => api_fetch_throws<promo_lift_review>(path))
  const [tab, set_tab] = useState("overview")

  if (!allowed || error) return <p class="p-6 text-sm text-muted-foreground">This report isn't available for your account.</p>
  if (!data) return <div class="p-6"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>

  const s = data.summary
  return (
    <div class="p-6 space-y-6 max-w-[1500px]">
      <header class="space-y-1">
        <h1 class="text-2xl font-semibold">Promo Lift Review</h1>
        <p class="max-w-3xl">
          A typical KeHE promo lifted shipments <Fig summary={s} field="kehe_tpr_median" as="signed_pct" /> against a plan of <Fig summary={s} field="kehe_tpr_plan_median" as="signed_pct" />, and Kroger's BOGOs roughly doubled scanned units (<Fig summary={s} field="kroger_lift" as="signed_pct" />). Shallow scans under $1.05 a unit showed no lift we could measure.
        </p>
        <p class="text-sm text-muted-foreground">
          Built from <Fig summary={s} field="matched_ded" as="int" /> deductions matched to <Fig summary={s} field="matched_promos" as="int" /> Promomash promotions. KeHE shipments run from {format(parseISO(String(s["weeks_from"])), "MMMM d")} to {format(parseISO(String(s["weeks_to"])), "MMMM d, yyyy")}. Kroger lift is read from Kroger's own scan billbacks.
        </p>
      </header>
      <Tabs value={tab} onValueChange={set_tab}>
        <TabsList className="h-auto flex-wrap">
          {TABS.map((item: { value: string; label: string }) => <TabsTrigger key={item.value} value={item.value}>{item.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
        <TabsContent value="kehe"><KeheTab data={data} /></TabsContent>
        <TabsContent value="kroger"><KrogerTab data={data} /></TabsContent>
        <TabsContent value="match"><MatchTab data={data} /></TabsContent>
        <TabsContent value="method"><MethodTab /></TabsContent>
      </Tabs>
    </div>
  )
}
